import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getAllProjects,
    getCloseDeals
} from '../../../builder/services/api/implementation/impl';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Table from '../../../shared/components/layout/Table';
import Search from '../../../shared/components/ui/Search';
import { transformData } from '../../utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

const CompanyProjects = () => {
    const { builderId, companyId } = useParams();
    const userId = Cookies.get('userId');
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // Fetch all projects
    // We fetch all because the existing API implies getting all and filtering
    const { data: projects, isFetching: isFetchingProjects } = useQuery({
        queryKey: ['allProjects'],
        queryFn: () => getAllProjects(),
        onError: (err) => {
            console.error('Error fetching projects:', err);
        }
    });

    // Fetch all closed deals
    const { data: closedDealsData, isFetching: isFetchingDeals } = useQuery({
        queryKey: ['allClosedDeals'],
        queryFn: () => getCloseDeals(null, 1, 9999),
        onError: (err) => {
            console.error('Error fetching closed deals:', err);
        }
    });

    // Process data for the table
    const processedData = useMemo(() => {
        if (!projects || !closedDealsData?.data || !companyId) return [];

        // Filter projects for this company
        const companyProjects = projects.filter(p => p.company_id === parseInt(companyId));

        return companyProjects.map((project) => {
            // Count closed deals for this project
            // Rule: deal.unit.project.id === project.id
            const closedDealsCount = closedDealsData.data.filter((deal) =>
                deal?.unit?.project?.id === project.id || deal.project_id === project.id
            ).length;

            return {
                id: project.id,
                project_name: project.name || 'N/A',
                closed_deals_count: closedDealsCount
            };
        });
    }, [projects, closedDealsData, companyId]);

    // Handle search
    const handleSearchChange = useCallback(
        (incomingQuery) => {
            setSearchQuery(incomingQuery);

            if (incomingQuery) {
                const lowerCaseQuery = incomingQuery.toLowerCase();
                const filtered = processedData.filter(
                    (project) =>
                        project.project_name?.toLowerCase().includes(lowerCaseQuery)
                );
                setFilteredData(filtered || []);
            } else {
                setFilteredData(processedData || []);
            }
        },
        [processedData]
    );

    // Update filteredData when processedData changes
    useEffect(() => {
        if (!searchQuery) {
            setFilteredData(processedData);
        }
    }, [processedData, searchQuery]);

    // Transform for table consumption
    const tableDataFormat = ['id', 'project_name', 'closed_deals_count'];
    const transformedData = filteredData.map((el) =>
        transformData(el, tableDataFormat)
    );

    const isLoading = isFetchingProjects || isFetchingDeals;

    // Handle row click
    const handleRowClick = (projectId) => {
        const project = filteredData.find(p => p.id === projectId);

        if (project && project.closed_deals_count > 0) {
            navigate(`/super-admin/closed-deals/${builderId}/companies/${companyId}/projects/${projectId}/deals`);
        } else {
            toast.error('No closed deals found for this project');
        }
    };

    return (
        <Layout>
            {/* Top Header */}
            <div className="fixed z-[20] top-0 left-[16.5vw] w-[83.5vw]">
                <Header data={{ name: 'Company Projects' }} backArrow={true} />
            </div>

            {/* Search */}
            <div className="flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] z-20 border-t-[2px] border-stone-100 w-[83vw] shadow-sm">
                <Search query={searchQuery} onChange={handleSearchChange} />
            </div>

            {/* Main Content - Table */}
            <GridWrapper width={100}>
                <div className="mt-[10rem] relative user-table-container h-[calc(100vh-11.5rem)] overflow-auto w-full">
                    <style>{`
              .user-table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .user-table-container .table-container {
                height: auto !important;
              }
              .user-table-container > div {
                height: auto !important;
              }
            `}</style>

                    {isLoading ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <Table
                            headers={['S.No', 'Project Name', 'Number of Closed Deals']}
                            data={transformedData.map((item, index) => ({
                                serialNumber: index + 1,
                                ...item
                            }))}
                            identifier={'id'}
                            handleRowClick={handleRowClick}
                        />
                    )}
                </div>
            </GridWrapper>
        </Layout>
    );
};

export default CompanyProjects;
